import { DocumentDto } from "@core/data/dto/document.dto";
import { PaginatedDto, PaginatedQuery } from "@core/data/dto/paginated.dto";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { Type } from "class-transformer";
import {
    IsArray,
    IsBoolean,
    IsDateString,
    IsEnum,
    IsInt,
    IsNotEmpty,
    IsNumber,
    IsObject,
    IsOptional,
    IsString,
    ValidateNested,
} from "class-validator";
import { KanbanTaskDto } from "../kanban/task.dto";

export enum ActivityStatusEnumDto {
    toDo = "not_started",
    inProgress = "in_progress",
    blocked = "blocked",
    done = "done",

    // Legacy backend value kept for backward compatibility with existing data.
    inTesting = "validation",
}

export enum ActivityPriorityEnumDto {
    low = "low",
    medium = "medium",
    high = "high",
}

export interface ActivitySlotDto {
    name: string;
    value: string;
}

export interface CreateTechnicalProposalIndicatorCommentBody {
    text: string;
    comment_type: string;
    reference_date?: Nullable<string>;
}

export class TechnicalProposalIndicatorCommentDto {
    @IsInt()
    @IsNotEmpty()
    readonly id!: number;

    @IsDateString()
    @IsNotEmpty()
    readonly created!: string;

    @IsDateString()
    @IsNotEmpty()
    readonly modified!: string;

    @IsString()
    @IsNotEmpty()
    readonly text!: string;

    @IsString()
    @IsNotEmpty()
    readonly comment_type!: string;

    @IsString()
    @IsOptional()
    readonly comment_type_label!: Undefinable<string>;

    @IsDateString()
    @IsOptional()
    readonly reference_date!: Nullable<string>;

    @IsInt()
    @IsNotEmpty()
    readonly indicator!: number;

    @IsString()
    @IsOptional()
    readonly created_by_name!: Nullable<string>;
}

export interface KanbanBlockDataDto {
    id: number;
    name: string;
    position: number;
    phase: string;
    project: number;
}

export class TechnicalProposalIndicatorDto {
    @IsInt()
    @IsNotEmpty()
    readonly id!: number;

    @IsDateString()
    @IsNotEmpty()
    readonly created!: string;

    @IsDateString()
    @IsNotEmpty()
    readonly modified!: string;

    @IsBoolean()
    @IsNotEmpty()
    readonly is_active!: boolean;

    @IsString()
    @IsOptional()
    readonly name!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly description!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly calculation_criteria!: Undefinable<string>;

    @IsString()
    @IsNotEmpty()
    readonly estimated_indicator!: string;

    @IsString()
    @IsOptional()
    readonly achieved_indicator!: Nullable<string>;

    @IsString()
    @IsOptional()
    readonly status!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly frequency!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly frequency_label!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly indicator_type!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly indicator_type_label!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly purpose_typology!: Nullable<string>;

    @IsString()
    @IsOptional()
    readonly purpose_typology_label!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly target_type!: Undefinable<string>;

    @IsString()
    @IsOptional()
    readonly target_type_label!: Undefinable<string>;

    @IsBoolean()
    @IsOptional()
    readonly disaggregate_by_gender!: Undefinable<boolean>;

    @IsObject()
    @IsOptional()
    readonly estimated_gender_breakdown!: Undefinable<
        Record<string, Nullable<string>>
    >;

    @IsObject()
    @IsOptional()
    readonly achieved_gender_breakdown!: Undefinable<
        Record<string, Nullable<string>>
    >;

    @IsObject()
    @IsOptional()
    readonly target_config!: Undefinable<Record<string, unknown>>;

    @IsString()
    @IsNotEmpty()
    readonly verification_source!: string;

    @IsInt()
    @IsOptional()
    readonly responsible_employee!: Nullable<number>;

    @IsString()
    @IsOptional()
    readonly responsible_employee_name!: Nullable<string>;

    @IsInt()
    @IsOptional()
    readonly comment_count!: Undefinable<number>;

    @IsString()
    @IsOptional()
    readonly latest_comment_text!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    readonly last_monitoring_date!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    readonly next_monitoring_due_date!: Nullable<string>;

    @IsBoolean()
    @IsOptional()
    readonly monitoring_update_overdue!: Undefinable<boolean>;

    @IsNumber()
    @IsOptional()
    readonly completion_percentage!: Undefinable<Nullable<number>>;

    @IsString()
    @IsOptional()
    readonly derived_status!: Undefinable<Nullable<string>>;

    @IsString()
    @IsOptional()
    readonly derived_status_code!: Undefinable<Nullable<string>>;

    @IsInt()
    @IsNotEmpty()
    readonly activity!: number;

    @IsString()
    @IsOptional()
    readonly activity_name!: Undefinable<string>;

    @IsArray()
    @ValidateNested({ each: true })
    @IsOptional()
    readonly activity_slots!: Undefinable<ActivitySlotDto[]>;

    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => KanbanTaskDto)
    @IsOptional()
    readonly tasks_data!: Undefinable<KanbanTaskDto[]>;
}

export class TechnicalProposalIndicatorsDto extends PaginatedDto {
    @IsArray()
    @IsNotEmpty()
    @Type(() => TechnicalProposalIndicatorDto)
    readonly results!: TechnicalProposalIndicatorDto[];
}

export interface IndicatorSearchFilters {
    project?: Undefinable<number>;
    objective?: Undefinable<number>;
    activity?: Undefinable<number>;
}

export type IndicatorsQuery = PaginatedQuery & {
    activity?: Undefinable<number>;
    project?: Undefinable<number>;
    objetive?: Undefinable<number>;
    expand?: string;
};

export class TechnicalProposalActivityDto {
    @IsInt()
    @IsNotEmpty()
    readonly id!: number;

    @IsArray()
    @Type(() => TechnicalProposalIndicatorDto)
    readonly indicators_data!: TechnicalProposalIndicatorDto[];

    @IsDateString()
    @IsOptional()
    readonly created!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    readonly modified!: Nullable<string>;

    @IsBoolean()
    @IsOptional()
    readonly is_active!: Nullable<boolean>;

    @IsString()
    @IsNotEmpty()
    readonly name!: string;

    @IsString()
    @IsNotEmpty()
    readonly description!: string;

    @IsString()
    readonly needs!: string;

    @IsDateString()
    @IsOptional()
    readonly start_date_estimated!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    readonly end_date_estimated!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    readonly start_date_real!: Nullable<string>;

    @IsDateString()
    @IsOptional()
    readonly end_date_real!: Nullable<string>;

    @IsInt()
    @IsNotEmpty()
    readonly objetive!: number;

    @IsInt()
    @IsOptional()
    readonly block!: Nullable<number>;

    @IsOptional()
    readonly block_data!: Nullable<KanbanBlockDataDto>;

    @IsArray()
    @IsInt({ each: true })
    @IsOptional()
    readonly employee!: Nullable<number[]>;

    @IsArray()
    @IsInt({ each: true })
    @IsOptional()
    readonly resources!: Nullable<number[]>;

    @IsArray()
    @IsInt({ each: true })
    @IsOptional()
    readonly dependencies!: Nullable<number[]>;

    @IsEnum(ActivityStatusEnumDto)
    @IsOptional()
    readonly status!: Nullable<ActivityStatusEnumDto>;

    @IsEnum(ActivityPriorityEnumDto)
    @IsOptional()
    readonly priority!: Nullable<ActivityPriorityEnumDto>;

    @IsInt()
    @IsOptional()
    readonly assigned_employee!: Nullable<number>;

    @IsString()
    @IsOptional()
    readonly deadline!: Nullable<string>;

    @IsOptional()
    readonly comments!: Nullable<string>;

    @IsInt()
    @IsOptional()
    readonly technical_proposal_id!: Nullable<number>;

    @IsArray()
    @IsOptional()
    @Type(() => KanbanTaskDto)
    readonly tasks_data!: Nullable<KanbanTaskDto[]>;
}

export interface EditProjectActivityBody {
    id: TechnicalProposalActivityDto["id"];

    name: TechnicalProposalActivityDto["name"];

    dependencies: Undefinable<
        NonNullable<TechnicalProposalActivityDto["dependencies"]>
    >;

    end_date_estimated: Undefinable<
        NonNullable<TechnicalProposalActivityDto["end_date_estimated"]>
    >;

    status: Undefinable<NonNullable<TechnicalProposalActivityDto["status"]>>;

    priority: Undefinable<
        NonNullable<TechnicalProposalActivityDto["priority"]>
    >;

    assigned_employee: Undefinable<
        NonNullable<TechnicalProposalActivityDto["assigned_employee"]>
    >;

    employee: Undefinable<
        NonNullable<TechnicalProposalActivityDto["employee"]>
    >;

    comments: Undefinable<
        NonNullable<TechnicalProposalActivityDto["comments"]>
    >;

    block: Undefinable<NonNullable<TechnicalProposalActivityDto["block"]>>;
}

export class TechnicalProposalActivitiesDto extends PaginatedDto {
    @IsArray()
    @IsNotEmpty()
    @Type(() => TechnicalProposalActivityDto)
    readonly results!: TechnicalProposalActivityDto[];
}

export interface ActivitySearchFilters {
    objetive?: Undefinable<number>;
    project?: Undefinable<number>;
}

export type ActivityQuery = PaginatedQuery & {
    objetive?: Undefinable<number>;
    project?: Undefinable<number>;
};

export class TechnicalProposalObjectiveDto {
    @IsNumber()
    @IsNotEmpty()
    readonly id!: number;

    @IsDateString()
    @IsNotEmpty()
    readonly created!: string;

    @IsDateString()
    @IsNotEmpty()
    readonly modified!: string;

    @IsBoolean()
    @IsNotEmpty()
    readonly is_active!: boolean;

    @IsString()
    @IsNotEmpty()
    readonly name!: string;

    @IsString()
    @IsNotEmpty()
    readonly description!: string;

    @IsNumber()
    @IsNotEmpty()
    readonly technical_proposal!: number;

    @IsArray()
    @Type(() => TechnicalProposalActivityDto)
    readonly activities_data!: TechnicalProposalActivityDto[];
}

export class TechnicalProposalObjectivesDto extends PaginatedDto {
    @IsArray()
    @IsNotEmpty()
    @Type(() => TechnicalProposalObjectiveDto)
    readonly results!: TechnicalProposalObjectiveDto[];
}

export interface ObjectiveSearchFilters {
    technicalProposal?: Undefinable<number>;
    project?: Undefinable<number>;
}

export type ObjectiveQuery = PaginatedQuery & {
    technical_proposal?: Undefinable<number>;
    project?: Undefinable<number>;
};

export class TechnicalProposalDto {
    @IsNumber()
    @IsNotEmpty()
    readonly id!: number;

    @IsDateString()
    @IsNotEmpty()
    readonly created!: string;

    @IsDateString()
    @IsNotEmpty()
    readonly modified!: string;

    @IsBoolean()
    @IsNotEmpty()
    readonly is_active!: boolean;

    @IsString()
    @IsNotEmpty()
    readonly overall_objetive!: string;

    @IsNumber()
    @IsNotEmpty()
    readonly project!: number;

    @IsArray()
    @IsOptional()
    @Type(() => TechnicalProposalObjectiveDto)
    readonly objetive_data!: Nullable<TechnicalProposalObjectiveDto[]>;

    @IsArray()
    @IsOptional()
    @Type(() => DocumentDto)
    readonly documents_data!: Nullable<DocumentDto[]>;

    @IsArray()
    @IsOptional()
    readonly documents!: Nullable<number[]>;
}

export class TechnicalProposalsDto extends PaginatedDto {
    @IsArray()
    @IsNotEmpty()
    @Type(() => TechnicalProposalDto)
    readonly results!: TechnicalProposalDto[];
}

export type TechnicalProposalsQuery = PaginatedQuery & {
    project?: Undefinable<number>;
};
